/**
 * The sentence for a refused signage save (`400 { error, assetId, blockers }`):
 * picks the `errors.json` code and its parameters from that body.
 */
import { translateKey, type Translate } from "../i18n/translate";
import { friendlyError } from "./apiError";

/** The shape `unwrap` throws: `code` is the server's `error`, `body` the rest. */
interface ThrownRefusal {
  code?: string;
  message?: string;
  body?: {
    error?: string;
    assetId?: string;
    blockers?: unknown[];
    webUrl?: string;
    landscape?: string[];
    portrait?: string[];
    conflicts?: Array<{
      unit?: string;
      unitOrientation?: string;
      content?: string;
      contentOrientation?: string;
    }>;
  };
}

const BLOCKER_CODES = new Set([
  "encrypted_not_licensable",
  "encrypted_no_content_id",
  "no_playable_manifest",
  "not_playable",
]);

/** One blocker code from `@shared/signage-content`, as a plain sentence. */
export function signageBlockerText(t: Translate, code: string): string {
  return BLOCKER_CODES.has(code)
    ? translateKey(t, `errors:signage_blocker_${code}`)
    : translateKey(t, "errors:signage_blocker_unknown", { code });
}

function orientationWord(t: Translate, value: string | undefined): string {
  const known =
    value === "landscape" || value === "portrait" || value === "mixed"
      ? value
      : "mixed";
  return translateKey(t, `signage:signageRefusal.orientation.${known}`);
}

/**
 * Turn a thrown save error into what the operator should read.
 *
 * `nameOf` lets the caller name the title the way the operator knows it (the
 * channel's name) instead of by its catalogue id; the id is the fallback,
 * because an id is still more useful than "one of your items".
 */
export function signageSaveErrorText(
  t: Translate,
  err: unknown,
  fallback: string,
  nameOf?: (assetId: string) => string | undefined
): string {
  const e = (err ?? {}) as ThrownRefusal;
  const code = e.code ?? e.body?.error;
  const assetId = e.body?.assetId ?? "";
  const name = assetId ? (nameOf?.(assetId) ?? assetId) : undefined;
  const named = (key: string, params: Record<string, unknown> = {}) =>
    name
      ? translateKey(t, `errors:${key}`, { ...params, name })
      : translateKey(t, `errors:${key}_unnamed`, params);

  switch (code) {
    case "content_not_playable_in_signage": {
      const first = e.body?.blockers?.[0] as { code?: unknown } | undefined;
      const blocker = typeof first?.code === "string" ? first.code : "";
      return named(code, { reason: signageBlockerText(t, blocker) });
    }
    case "content_not_found":
      return named(code);
    case "signage_url_unsafe":
      return e.body?.webUrl
        ? translateKey(t, "errors:signage_url_unsafe", {
            webUrl: e.body.webUrl,
          })
        : translateKey(t, "errors:signage_url_unsafe_no_address");
    case "media_not_found":
    case "slide_not_found":
    case "orientation_mixed":
    case "template_orientation_locked":
      return translateKey(t, `errors:${code}`);
    case "orientation_conflict": {
      // UNIT-3: content must fit every unit it reaches.
      const c = e.body?.conflicts?.[0];
      const more = (e.body?.conflicts?.length ?? 0) - 1;
      if (!c) return translateKey(t, "errors:orientation_conflict_unknown");
      const params = {
        content: c.content,
        unit: c.unit,
        contentOrientation: orientationWord(t, c.contentOrientation),
        unitOrientation: orientationWord(t, c.unitOrientation),
      };
      return more > 0
        ? translateKey(t, "errors:orientation_conflict_detail_more", {
            ...params,
            count: more,
          })
        : translateKey(t, "errors:orientation_conflict_detail", params);
    }
    default:
      return friendlyError(t, err, fallback);
  }
}
